/**
 * @voltxampere/shared — VXAError abstract base
 *
 * Every concrete error in the platform extends this class via one of
 * the five category abstracts (UserError, EngineError, StorageError,
 * PluginError, SystemError). The base is responsible for:
 *
 *  - carrying a stable `code` literal (SPECS §5)
 *  - fixing the prototype chain so `instanceof` survives minification
 *    and cross-module boundaries (TypeScript Error-subclassing gotcha)
 *  - serialising cleanly for the Worker `postMessage` boundary
 *  - freezing `meta` so callers can't mutate it after the fact
 *
 * See BLUEPRINT §18.2 for the design rationale; this implementation
 * is the SPECS §5-aligned refinement of that template.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
export class VXAError extends Error {
    severity;
    meta;
    timestamp;
    i18nKeyOverride;
    constructor(message, options) {
        super(message, options?.cause !== undefined ? { cause: options.cause } : undefined);
        this.name = this.constructor.name;
        this.severity = options?.severity ?? this.defaultSeverity();
        // NOTE: `i18nKey` is exposed via a getter instead of a field
        // because `defaultI18nKey()` reads `this.code`, which is
        // initialised by subclass field initialisers AFTER this
        // constructor returns. Storing only the override here sidesteps
        // the well-known TS Error-subclass init-order gotcha.
        this.i18nKeyOverride = options?.i18nKey;
        this.meta = options?.meta !== undefined ? Object.freeze({ ...options.meta }) : undefined;
        this.timestamp = Date.now();
        // Restore prototype chain — Error subclassing loses it under some
        // transpile targets and breaks `instanceof`.
        Object.setPrototypeOf(this, new.target.prototype);
    }
    get i18nKey() {
        return this.i18nKeyOverride ?? this.defaultI18nKey();
    }
    /** Default i18n key is `errors.<CODE>.message`. Leafs may override. */
    defaultI18nKey() {
        return `errors.${this.code}.message`;
    }
    /** Worker-safe serialised form. No `stack`, no live references. */
    toJSON() {
        const out = {
            name: this.name,
            code: this.code,
            category: this.category,
            severity: this.severity,
            recoverable: this.recoverable,
            message: this.message,
            i18nKey: this.i18nKey, // getter — reads this.code at access time
            timestamp: this.timestamp,
        };
        if (this.meta !== undefined) {
            out.meta = this.meta;
        }
        const serialisedCause = this.serialiseCause();
        if (serialisedCause !== undefined) {
            out.cause = serialisedCause;
        }
        return out;
    }
    serialiseCause() {
        const raw = this.cause;
        if (raw === undefined)
            return undefined;
        if (raw instanceof VXAError)
            return raw.toJSON();
        if (raw instanceof Error)
            return { name: raw.name, message: raw.message };
        return undefined;
    }
}
//# sourceMappingURL=base.js.map